import React, { useEffect, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeSeriesScale
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import dayjs from 'dayjs';
import ErrorCard from '../ErrorCard/ErrorCard';
import { determineTimeUnit, generateChartOptions } from './chartOptions';
import useStyles from './useStyles';
import generateColours from '../generateColours';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeSeriesScale
);

interface GenericLineChartProps<T> {
  data: T[] | undefined;
  xAxisLabel?: string;
  yAxisLabel?: string;
  height?: number;
  dateKey: keyof T;
  groupKey?: keyof T;
  groupKeys?: (keyof T)[];
  countKey?: keyof T;
  dateRange?: [dayjs.Dayjs | null, dayjs.Dayjs | null];
  isSmallScreen: boolean;
  selectedGroups: string[];
  groupKeyLabels?: Record<string, string>;
  onDataProcessed?: (groupNames: string[], initialSelection: string[]) => void;
  hideLegend?: boolean;
}

function GenericLineChart<T>({
  data,
  xAxisLabel,
  yAxisLabel,
  height,
  dateKey,
  groupKey,
  groupKeys,
  countKey,
  dateRange,
  isSmallScreen,
  selectedGroups,
  groupKeyLabels,
  onDataProcessed,
  hideLegend
}: GenericLineChartProps<T>): React.ReactElement | null {
  const [formattedData, setFormattedData] = useState<any>(null);
  const [allYValues, setAllYValues] = useState<number[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [groupNames, setGroupNames] = useState<string[]>([]);

  const styles = useStyles(isSmallScreen);

  useEffect(() => {
    if (data) {
      if (groupKeys && groupKeys.length > 0) {
        const groupKeysStrings = groupKeys.map((key) => String(key));
        setGroupNames(groupKeysStrings);
        onDataProcessed?.(groupKeysStrings, groupKeysStrings.slice(0, 5));
      } else if (groupKey && countKey) {
        const groupTotals = data.reduce(
          (acc, item) => {
            const group = String(item[groupKey]);
            const count = Number(item[countKey]) || 0;
            acc[group] = (acc[group] || 0) + count;
            return acc;
          },
          {} as Record<string, number>
        );
        const sortedGroups = Array.from(
          new Set(data.map((item) => String(item[groupKey])))
        ).sort((a, b) => (groupTotals[b] || 0) - (groupTotals[a] || 0));
        const initialSelection = sortedGroups.slice(0, 5);
        setGroupNames(sortedGroups);
        onDataProcessed?.(sortedGroups, initialSelection);
      } else {
        setErrorMessage(
          'Either groupKey and countKey or groupKeys must be provided.'
        );
      }
    }
  }, [data, groupKey, groupKeys, countKey, onDataProcessed]);

  useEffect(() => {
    if (data && groupNames.length > 0) {
      const allYValuesTemp: number[] = [];
      let groupedData: Record<string, { x: Date; y: number }[]> = {};

      if (groupKeys && groupKeys.length > 0) {
        groupKeys.forEach((key) => {
          const groupData = data.map((item) => {
            const xValue = new Date(String(item[dateKey]));
            const yValueRaw = item[key];
            const yValue = Number(yValueRaw);
            allYValuesTemp.push(yValue);
            return { x: xValue, y: yValue };
          });
          groupedData[key as string] = groupData;
        });
      } else if (groupKey && countKey) {
        groupedData = data.reduce(
          (acc, item) => {
            const xValue = new Date(String(item[dateKey]));
            const groupValue = String(item[groupKey]);
            const yValueRaw = item[countKey];
            const yValue = Number(yValueRaw);
            if (!acc[groupValue]) {
              acc[groupValue] = [];
            }
            acc[groupValue].push({ x: xValue, y: yValue });
            allYValuesTemp.push(yValue);
            return acc;
          },
          {} as Record<string, { x: Date; y: number }[]>
        );
      } else {
        setErrorMessage(
          'Either groupKey and countKey or groupKeys must be provided.'
        );
        return;
      }

      const colours = generateColours(groupNames.length);

      const datasets = groupNames.map((group, index) => {
        const colour: string = colours[index];
        const label = groupKeyLabels ? groupKeyLabels[group] || group : group;
        return {
          label,
          data: groupedData[group] || [],
          backgroundColor: colour,
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderColor: colour,
          pointBackgroundColor: colour,
          pointBorderColor: 'white',
          pointBorderWidth: 1,
          pointHoverBackgroundColor: colour,
          pointHoverBorderColor: 'white',
          pointHoverBorderWidth: 2,
          hidden: !selectedGroups.includes(group)
        };
      });

      setFormattedData({
        datasets
      });
      setAllYValues(allYValuesTemp);
    }
  }, [
    data,
    dateKey,
    groupKey,
    groupKeys,
    countKey,
    selectedGroups,
    groupNames,
    groupKeyLabels
  ]);

  const maxYValue = selectedGroups.length
    ? Math.max(
        ...allYValues.filter((_, index) =>
          selectedGroups.includes(groupNames[index])
        )
      )
    : 1;

  const timeUnit = useMemo(() => {
    let startDate: Date | null = null;
    let endDate: Date | null = null;

    if (dateRange?.[0] && dateRange?.[1]) {
      startDate = dateRange[0].toDate();
      endDate = dateRange[1].toDate();
    } else if (data && data.length > 0) {
      const dates = data.map((item) => new Date(String(item[dateKey])));
      startDate = new Date(Math.min(...dates.map((d) => d.getTime())));
      endDate = new Date(Math.max(...dates.map((d) => d.getTime())));
    }
    if (!startDate || !endDate) {
      return 'day';
    }
    return determineTimeUnit(startDate, endDate);
  }, [data, dateRange, dateKey]);

  if (errorMessage) {
    return <ErrorCard errorMessage={errorMessage} />;
  }

  if (!formattedData) {
    return (
      <div role='img' id='chart-container' style={{ height, width: '100%' }} />
    );
  }

  const options = generateChartOptions(
    xAxisLabel,
    yAxisLabel,
    maxYValue,
    timeUnit,
    styles.cardPadding,
    dateRange,
    isSmallScreen,
    hideLegend
  );

  return (
    <div role='img' id='chart-container' style={{ height, width: '100%' }}>
      <Line data={formattedData} options={options} id='chart' />
    </div>
  );
}

export default GenericLineChart;
